/**
 * User datapack store (IndexedDB).
 * Local storage for characters and campaigns. Used when app runs in local-first mode;
 * for Pro-DM campaigns, server may still hold the authoritative copy.
 */

const DB_NAME = 'mythkit_userdata';
const DB_VERSION = 1;
const STORE_CHARACTERS = 'characters';
const STORE_CAMPAIGNS = 'campaigns';

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_CHARACTERS)) {
        db.createObjectStore(STORE_CHARACTERS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_CAMPAIGNS)) {
        db.createObjectStore(STORE_CAMPAIGNS, { keyPath: 'id' });
      }
    };
  });
  return dbPromise;
}

function generateId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `local_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

// --- Characters ---

export async function getAllCharacters() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_CHARACTERS, 'readonly');
      const req = tx.objectStore(STORE_CHARACTERS).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return [];
  }
}

export async function getCharacter(id) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_CHARACTERS, 'readonly');
      const req = tx.objectStore(STORE_CHARACTERS).get(id);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return null;
  }
}

export async function putCharacter(character) {
  const id = character.id || generateId();
  const now = new Date().toISOString();
  const doc = {
    ...character,
    id: String(id),
    updated_at: character.updated_at || now,
    created_at: character.created_at || now,
  };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CHARACTERS, 'readwrite');
    const req = tx.objectStore(STORE_CHARACTERS).put(doc);
    req.onsuccess = () => resolve(doc);
    req.onerror = () => reject(req.error);
  });
}

export async function removeCharacter(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CHARACTERS, 'readwrite');
    const req = tx.objectStore(STORE_CHARACTERS).delete(String(id));
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Clear store and replace with the given list (e.g. from server). Empty list = clear all. */
export async function replaceAllCharacters(list) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_CHARACTERS, 'readwrite');
      const store = tx.objectStore(STORE_CHARACTERS);
      store.clear();
      const items = Array.isArray(list) ? list : [];
      items.forEach((char) => {
        const id = char.id || generateId();
        const now = new Date().toISOString();
        const doc = {
          ...char,
          id: String(id),
          updated_at: char.updated_at || now,
          created_at: char.created_at || now,
        };
        store.put(doc);
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    // no-op on failure
  }
}

// --- Campaigns ---

export async function getAllCampaigns() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_CAMPAIGNS, 'readonly');
      const req = tx.objectStore(STORE_CAMPAIGNS).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return [];
  }
}

export async function getCampaign(id) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_CAMPAIGNS, 'readonly');
      const req = tx.objectStore(STORE_CAMPAIGNS).get(String(id));
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return null;
  }
}

export async function putCampaign(campaign) {
  const id = campaign.id || generateId();
  const now = new Date().toISOString();
  const doc = {
    ...campaign,
    id: String(id),
    updated_at: campaign.updated_at || now,
    created_at: campaign.created_at || now,
  };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CAMPAIGNS, 'readwrite');
    const req = tx.objectStore(STORE_CAMPAIGNS).put(doc);
    req.onsuccess = () => resolve(doc);
    req.onerror = () => reject(req.error);
  });
}

export async function removeCampaign(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CAMPAIGNS, 'readwrite');
    const req = tx.objectStore(STORE_CAMPAIGNS).delete(String(id));
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Clear store and replace with the given list (e.g. from server). Empty list = clear all. */
export async function replaceAllCampaigns(list) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_CAMPAIGNS, 'readwrite');
      const store = tx.objectStore(STORE_CAMPAIGNS);
      store.clear();
      const items = Array.isArray(list) ? list : [];
      items.forEach((camp) => {
        const id = camp.id || generateId();
        const now = new Date().toISOString();
        const doc = {
          ...camp,
          id: String(id),
          updated_at: camp.updated_at || now,
          created_at: camp.created_at || now,
        };
        store.put(doc);
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    // no-op on failure
  }
}
